import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { AttachMoney as AttachMoneyIcon } from '@mui/icons-material';
import {
  amountInputStep,
  getAutoReloadRules,
  getBusinessAutoReloadAmount,
  validateConfiguredAmount,
} from 'utils/businessPaymentRules';

const AUTO_RELOAD_AMOUNT_SUBJECT = 'an automatic reload amount';

export const FORM_COLUMN_AUTO_RELOAD_SX = {
  width: { xs: '100%', md: 'calc(50% - 12px)' },
  boxSizing: 'border-box',
};

export const useAutoReloadPreference = (business, defaultEnabled = true) => {
  const rules = getAutoReloadRules(business);
  const currentAmount = getBusinessAutoReloadAmount(business);
  const [initialized, setInitialized] = useState(false);
  const [enabled, setEnabled] = useState(defaultEnabled);
  const [inputValue, setInputValue] = useState('');
  const [amount, setAmount] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialized || !rules) {
      return;
    }
    const initialAmount = currentAmount ?? rules.defaultAmount;
    const value = String(initialAmount);
    const validation = validateConfiguredAmount(
      value,
      rules,
      AUTO_RELOAD_AMOUNT_SUBJECT
    );
    setEnabled(currentAmount !== null || defaultEnabled);
    setInputValue(value);
    setAmount(validation.amount);
    setError(validation.error);
    setInitialized(true);
  }, [currentAmount, defaultEnabled, initialized, rules]);

  const handleAmountChange = (event) => {
    const value = event.target.value;
    const validation = validateConfiguredAmount(
      value,
      rules,
      AUTO_RELOAD_AMOUNT_SUBJECT
    );
    setInputValue(value);
    setAmount(validation.amount);
    setError(validation.error);
  };

  return {
    amount,
    enabled,
    error,
    initialized,
    inputValue,
    isValid: initialized && (!enabled || (amount !== null && !error)),
    reloadAmount: enabled ? amount : 0,
    rules,
    setEnabled,
    handleAmountChange,
  };
};

const AutoReloadPreference = ({ preference, sx }) => {
  const {
    enabled,
    error,
    initialized,
    inputValue,
    rules,
    setEnabled,
    handleAmountChange,
  } = preference;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        ...sx,
      }}
    >
      <Typography variant="subtitle1">Auto reload</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add funds automatically when your account balance is low.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'auto minmax(0, 1fr)' },
          alignItems: 'start',
          columnGap: 4,
          rowGap: 2,
        }}
      >
        <FormControlLabel
          control={(
            <Switch
              checked={enabled}
              disabled={!initialized}
              onChange={(event) => setEnabled(event.target.checked)}
              inputProps={{ 'aria-label': 'Auto reload' }}
            />
          )}
          label={enabled ? 'On' : 'Off'}
          sx={{ m: 0, minHeight: 56, minWidth: 96 }}
        />
        <TextField
          label="Reload amount"
          type="number"
          value={inputValue}
          onChange={handleAmountChange}
          disabled={!initialized || !enabled}
          error={enabled && !!error}
          helperText={enabled ? (error || 'Charged when auto reload runs.') : 'Auto reload is off.'}
          fullWidth
          inputProps={{
            min: rules?.minimumAmount,
            max: rules?.maximumAmount,
            step: rules ? amountInputStep(rules.maximumScale) : undefined,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AttachMoneyIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default AutoReloadPreference;
